import { cn } from "@/lib/utils";
import { VariantProps } from "class-variance-authority";
import React from "react";
import { buttonVariants } from "./button-variants";

export interface GradientBorderButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  children: string | React.ReactNode;
  icon?: React.ReactNode;
  iconPosition?: "leading" | "trailing";
  variant: "gradientBorder" | "gradientBorderSubtle";
}

const GradientBorderButton = React.forwardRef<
  HTMLButtonElement,
  GradientBorderButtonProps
>(
  (
    {
      children,
      variant,
      size,
      className,
      icon,
      iconPosition = "trailing",
      ...props
    },
    ref,
  ) => {
    const isIconChildren = typeof children !== "string";
    const iconElement = icon ? (
      <span className="relative [&_svg]:size-4 [&_svg]:stroke-[url(#icon-gradient)]">
        {icon}
      </span>
    ) : null;

    return (
      <button
        className={cn(
          buttonVariants({
            variant,
            size,
            className: cn({
              "pl-4": icon && iconPosition === "leading",
              "pr-4": icon && iconPosition === "trailing",
              "flex items-center gap-2.5": icon,
            }),
          }),
          "border border-transparent",
          className,
        )}
        style={{
          background:
            variant === "gradientBorderSubtle"
              ? "linear-gradient(to bottom right, var(--color-primary-100), var(--color-secondary-100)) padding-box, linear-gradient(to bottom right, color-mix(in srgb, var(--color-primary-400) 10%, transparent), color-mix(in srgb, var(--color-secondary-400) 10%, transparent)) border-box"
              : "linear-gradient(to bottom right, var(--color-primary-100), var(--color-secondary-100)) padding-box, linear-gradient(to bottom right, var(--color-primary-400), var(--color-secondary-400)) border-box",
        }}
        ref={ref}
        {...props}
      >
        {iconElement && iconPosition === "leading" && iconElement}

        <span
          className={cn({
            "relative [&_svg]:size-4 [&_svg]:stroke-[url(#icon-gradient)]":
              isIconChildren,
            "relative flex items-center justify-center bg-linear-to-r from-primary-400 to-secondary-400 bg-clip-text text-transparent":
              !isIconChildren,
          })}
        >
          {children}
        </span>

        {iconElement && iconPosition === "trailing" && iconElement}
      </button>
    );
  },
);

GradientBorderButton.displayName = "GradientBorderButton";

export { GradientBorderButton };
